import { ChevronDown } from "lucide-react";
import { FAQ_ITEMS, type FaqItem } from "@/lib/landing/faq";
import { makeTr, type LandingLocale, pick } from "@/lib/landing/locale";
import { Container, type ContourVariant, Section, SectionHeading } from "./primitives";

/**
 * The FAQ from src/lib/landing/faq.ts (the same entries feed the FAQPage
 * JSON-LD), or a guide page's own questions. Native <details>, so every
 * answer is in the HTML and works without JavaScript.
 */
export function FaqSection({
  locale,
  items = FAQ_ITEMS,
  lead,
  tone = "plain",
  contours = "column",
}: {
  locale: LandingLocale;
  /**
   * Plain with contours, or tinted where the page's bands need it
   * (`tintedToClosing` when the closing block follows directly).
   */
  tone?: "plain" | "tinted" | "tintedToClosing";
  /** The contour variant when plain (tinted bands carry none). */
  contours?: ContourVariant;
  items?: readonly FaqItem[];
  lead?: string;
}) {
  const tr = makeTr(locale);

  return (
    <Section
      id="faq"
      labelledBy="faq-title"
      tone={tone}
      contours={tone === "plain" ? contours : undefined}
    >
      <Container className="grid gap-10 lg:grid-cols-[1fr_2fr] lg:gap-16">
        <SectionHeading
          id="faq-title"
          title={tr("Časté dotazy", "Frequently asked questions")}
          lead={
            lead ??
            tr(
              "Na co se lidé ptají, než Fio účet připojí.",
              "What people ask before they connect their Fio account."
            )
          }
        />
        <div className="divide-y overflow-hidden rounded-xl border bg-card">
          {items.map((item) => (
            <details
              key={item.id}
              id={`faq-${item.id}`}
              className="group scroll-mt-28 md:scroll-mt-20"
            >
              <summary className="flex cursor-pointer list-none items-center justify-between gap-4 px-5 py-4 text-base font-medium transition-colors hover:bg-secondary/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[hsl(var(--action))] dark:focus-visible:ring-primary [&::-webkit-details-marker]:hidden">
                <span>{pick(item.question, locale)}</span>
                <ChevronDown
                  aria-hidden="true"
                  className="h-4 w-4 shrink-0 text-muted-foreground transition-transform group-open:rotate-180"
                />
              </summary>
              <p className="px-5 pb-5 text-sm leading-relaxed text-muted-foreground">
                {pick(item.answer, locale)}
              </p>
            </details>
          ))}
        </div>
      </Container>
    </Section>
  );
}
